/**
 * Service frontend pour le portail abonné (Espace Client de paiement de factures).
 * Utilise un JWT séparé du staff stocké dans localStorage.portail_token.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

const PORTAIL_TOKEN_KEY = 'portail_token';

export const portailAuth = {
  setToken(token: string) { localStorage.setItem(PORTAIL_TOKEN_KEY, token); },
  getToken(): string | null { return localStorage.getItem(PORTAIL_TOKEN_KEY); },
  clear() { localStorage.removeItem(PORTAIL_TOKEN_KEY); localStorage.removeItem('portail_abonne'); },
  setAbonne(a: any) { localStorage.setItem('portail_abonne', JSON.stringify(a)); },
  getAbonne(): any { try { return JSON.parse(localStorage.getItem('portail_abonne') || 'null'); } catch { return null; } },
  isAuthenticated(): boolean { return !!this.getToken(); },
};

const authHeader = () => ({
  Authorization: `Bearer ${portailAuth.getToken() || ''}`,
});

export type TypeFacturePortail =
  | 'EAU' | 'ELECTRICITE' | 'MARCHANDISE' | 'TELEPHONIE' | 'INTERNET'
  | 'PRESTATION' | 'COTISATION' | 'LOYER' | 'FRAIS_SCOLAIRE' | 'AUTRE';

export const TYPE_FACTURE_LABELS: Record<TypeFacturePortail, string> = {
  EAU: 'Eau', ELECTRICITE: 'Électricité', MARCHANDISE: 'Marchandise',
  TELEPHONIE: 'Téléphonie', INTERNET: 'Internet', PRESTATION: 'Prestation',
  COTISATION: 'Cotisation', LOYER: 'Loyer', FRAIS_SCOLAIRE: 'Frais scolaires', AUTRE: 'Autre',
};

export type CanalPaiement =
  | 'ORANGE_MONEY' | 'MTN_MOMO' | 'WAVE' | 'CARTE' | 'VIREMENT' | 'ESPECES_AGENCE' | 'AUTRE';

export type StatutFacture = 'EN_COURS' | 'ECHUE' | 'PAYEE' | 'PARTIELLE' | 'CONTESTEE' | 'ANNULEE';

export type StatutPaiement =
  | 'INIT' | 'EN_COURS' | 'CONFIRME' | 'ECHEC' | 'ANNULE' | 'EXPIRE' | 'REMBOURSE';

export interface AbonnementPortail {
  numero: string | null;
  reseau?: string | null;
  statut?: string | null;
  typeProduit?: string | null;
  typeComptage?: string | null;
  numeroPds?: string | null;
  numeroCompteur?: string | null;
  numeroBranchement?: string | null;
  dateAbonnement?: string | null;
  dateEcheance?: string | null;
  groupeFacturation?: string | null;
  modeFacturation?: string | null;
  regroupement?: string | null;
  puissanceSouscrite?: number | string | null;
}

export interface BranchementPortail {
  numero: string | null;
  puissanceSouscrite?: number | string | null;
  statut?: string | null;
  numeroCompteur?: string | null;
  adresse?: string | null;
  typeCompteur?: string | null;
  reseau?: string | null;
  departHta?: string | null;
  transfoHtaBt?: string | null;
  poteau?: string | null;
}

/**
 * Catégories de factures EEC exposées dans le portail abonné.
 * - ENERGIE   : factures d'électricité (table ecom_factures_electriques, source SIC)
 * - TRAVAUX   : factures liées aux travaux de branchement / extension
 * - PRESTATION: factures de prestations de service (études, relevés, …)
 * - MATERIEL  : factures de vente de matériels électriques
 * - CAUTION   : dépôts de garantie / cautions
 */
export type CategorieFactureUtil =
  | 'ENERGIE'
  | 'TRAVAUX'
  | 'PRESTATION'
  | 'MATERIEL'
  | 'CAUTION';

export interface FacturePortail {
  id: string;
  numeroFacture: string;
  periodeDebut?: string;
  periodeFin?: string;
  dateEmission?: string;
  dateEcheance?: string;
  montantTTC: number;
  montantPaye: number;
  montantRestant: number;
  devise: string;
  statut: StatutFacture;
  libelle?: string;
  consommation?: number;
  uniteConsommation?: string;
  avisCoupure?: boolean;
  /**
   * Catégorie EEC — renvoyée par le backend si disponible.
   * Si absente, le frontend l'infère depuis `libelle` / `objet` / `sourceModule`.
   */
  categorieUtil?: CategorieFactureUtil;
  /** Champ optionnel issu de FactureData.objet (backend unifié) */
  objet?: string;
  /** Champ optionnel issu de FactureData.sourceModule */
  sourceModule?: string;

  // ── Détail facture d'énergie (source ecom_factures_electriques) ───────────
  /** Libellé de la période de facturation (ex. « Août 2026 »). */
  periodeLibelle?: string;
  /** Ancien index du compteur au relevé. */
  indexAncien?: number;
  /** Nouvel index du compteur au relevé. */
  indexNouveau?: number;
  /** Date du relevé ayant servi à la facturation. */
  dateReleve?: string;
  montantHT?: number;
  montantTVA?: number;
  montantPenalites?: number;
  montantTaxesAnnexes?: number;
  impayesAnterieurs?: number;
  penalitesRetard?: number;
}

export interface CanalDispo {
  id: string;
  canal: CanalPaiement;
  passerelle: string;
  libelle: string;
  logoUrl?: string;
  fraisPourcent: number;
  fraisFixe: number;
  fraisACharge: 'CLIENT' | 'ENTITE';
  montantMin: number;
  montantMax: number;
  ordre: number;
}

export interface PaiementPortail {
  id: string;
  numeroTransaction: string;
  factures: Array<{ factureId: string; numero: string; montantImpute: number }>;
  montantTotal: number;
  frais: number;
  devise: string;
  canal: CanalPaiement;
  passerelle?: string;
  statut: StatutPaiement;
  dateInit?: string;
  dateConfirmation?: string;
  recuPdfUrl?: string;
}

/**
 * Template d'activité exposé par le backend pour adapter le portail au métier
 * de l'entité (eau, électricité, internet, téléphonie, école, immobilier,
 * générique). Pilote le menu de navigation et les messages contextuels.
 */
export type TemplateActivite =
  | 'EAU' | 'ELECTRICITE' | 'INTERNET' | 'TELEPHONIE'
  | 'ECOLE' | 'IMMOBILIER'
  /** Mai 2026 — Cabinet juridique (avocat / huissier / notaire) */
  | 'CABINET'
  /** Août 2026 — Fonds d'épargne à adhérents (souscripteurs, versements, capitalisation) */
  | 'FONDS_EPARGNE'
  /** Août 2026 — Organisation patronale : ses membres sont des adhérents. */
  | 'PATRONAT'
  | 'GENERIQUE';

export interface EntitePortailInfo {
  id: string;
  slug: string;
  nom: string;
  type?: string;
  pays?: string;
  telephone?: string;
  email?: string;
  ville?: string;
  adresse?: string;
  logo?: string | null;
  /** Type d'activité de l'entité — guide le rendu du portail. */
  templateActivite?: TemplateActivite;
  typesFacture: Array<{ type: TypeFacturePortail; libelleIdentifiant: string; formatIdentifiant?: string }>;
  nbCanauxActifs: number;
  portailActif: boolean;
  /** Vrai si l'entité a souscrit au module espace_client dans son pack ou via modulesActifs */
  moduleSouscrit?: boolean;
  /** Message explicatif renvoyé si moduleSouscrit=false */
  message?: string;
  /**
   * Oct. 2026 — Onglets du portail décrits en base pour cette entité
   * (référentiel `sc_metier.ecom_portail_metiers` + table des onglets par
   * métier, cf. `portailOnglets.ts`). Absent ⇒ repli produit, puis menu
   * historique : aucun portail en service ne change de forme.
   */
  ongletsPortail?: Array<{
    slug: string;
    libelle: string;
    icone?: string | null;
    groupe?: string | null;
    ordre?: number | null;
    badge?: string | null;
    visibleProspect?: boolean | null;
  }> | null;
  diagnostic?: {
    connecteursSicConfigures: number;
    canauxPaiementActifs: number;
    templateActivite?: TemplateActivite;
    /** SIC requis pour ce template (faux pour immobilier/école/générique). */
    sicRequis?: boolean;
    /**
     * Oct. 2026 — Code métier résolu par le référentiel du portail
     * (`ecom_portail_metiers` / `ecom_portail_metier_entite`). C'est lui, et
     * non plus le template deviné, qui doit commander le jeu d'onglets.
     */
    metier?: string;
    metierLibelle?: string;
    metierOrigine?: 'ENTITE' | 'MOTS_CLES' | 'TEMPLATE' | 'DEFAUT';
    metierReferentielParDefaut?: boolean;
  };
}

class EspaceClientService {
  // ─── Résolution entité par slug (page de login/inscription) ─────────
  async resoudreEntite(slug: string): Promise<EntitePortailInfo> {
    const r = await axios.get(`${API_URL}/portail/entite/${encodeURIComponent(slug)}`);
    return r.data;
  }

  // ─── Authentification ───────────────────────────────────────────────
  async inscrire(dto: {
    entiteId: string;
    typeFacture: TypeFacturePortail;
    identifiantClient: string;
    nom?: string; prenom?: string;
    email: string; telephone: string;
    motDePasse: string;
    donneesValidation?: any;
  }): Promise<{ abonneId: string; otpEnvoye: boolean; canal: string }> {
    const r = await axios.post(`${API_URL}/portail/inscription`, dto);
    return r.data;
  }

  async verifierOtp(abonneId: string, code: string) {
    const r = await axios.post(`${API_URL}/portail/verifier-otp`, { abonneId, code });
    return r.data;
  }

  async renvoyerOtp(abonneId: string) {
    const r = await axios.post(`${API_URL}/portail/renvoyer-otp`, { abonneId });
    return r.data;
  }

  async login(dto: {
    entiteId: string; typeFacture: TypeFacturePortail;
    identifiantClient: string; motDePasse: string;
  }): Promise<{ token: string; abonne: any }> {
    const r = await axios.post(`${API_URL}/portail/login`, dto);
    portailAuth.setToken(r.data.token);
    portailAuth.setAbonne(r.data.abonne);
    return r.data;
  }

  logout() { portailAuth.clear(); }

  async motDePasseOublie(entiteId: string, typeFacture: TypeFacturePortail, identifiantClient: string) {
    const r = await axios.post(`${API_URL}/portail/mot-de-passe-oublie`, {
      entiteId, typeFacture, identifiantClient,
    });
    return r.data;
  }

  async reinitialiser(token: string, nouveauMotDePasse: string) {
    const r = await axios.post(`${API_URL}/portail/reinitialiser`, { token, nouveauMotDePasse });
    return r.data;
  }

  async changerMotDePasse(ancien: string, nouveau: string) {
    const r = await axios.post(`${API_URL}/portail/changer-mot-de-passe`, { ancien, nouveau }, { headers: authHeader() });
    return r.data;
  }

  // ─── Factures ───────────────────────────────────────────────────────
  async listerFactures(opts?: { statut?: StatutFacture; refresh?: boolean }): Promise<FacturePortail[]> {
    const r = await axios.get(`${API_URL}/portail/factures`, {
      headers: authHeader(),
      params: opts,
    });
    return r.data;
  }

  async detailFacture(id: string): Promise<FacturePortail> {
    const r = await axios.get(`${API_URL}/portail/factures/${id}`, { headers: authHeader() });
    return r.data;
  }

  // ── Mon contrat (abonnements & branchements de l'abonné connecté) ─────────
  async listerAbonnements(): Promise<AbonnementPortail[]> {
    const r = await axios.get(`${API_URL}/portail/abonnements`, { headers: authHeader() });
    return Array.isArray(r.data) ? r.data : [];
  }

  async listerBranchements(): Promise<BranchementPortail[]> {
    const r = await axios.get(`${API_URL}/portail/branchements`, { headers: authHeader() });
    return Array.isArray(r.data) ? r.data : [];
  }

  pdfFactureUrl(id: string): string {
    // Injecte le token portail en query param pour permettre l'ouverture
    // directe dans un onglet navigateur (guard accepte ?token= OU header Bearer)
    const token = portailAuth.getToken();
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_URL}/portail/factures/${id}/pdf${q}`;
  }

  // ─── Paiements ──────────────────────────────────────────────────────
  async listerCanaux(montant?: number): Promise<CanalDispo[]> {
    const r = await axios.get(`${API_URL}/portail/canaux`, {
      headers: authHeader(),
      params: montant ? { montant } : {},
    });
    return r.data;
  }

  async initierPaiement(dto: {
    factureIds: string[];
    canal: CanalPaiement;
    montantPartiel?: number;
    idempotencyKey?: string;
  }): Promise<{ paiementId: string; numeroTransaction: string; redirectUrl?: string; instructions?: string }> {
    const r = await axios.post(`${API_URL}/portail/paiements/initier`, dto, { headers: authHeader() });
    return r.data;
  }

  async getPaiement(id: string): Promise<PaiementPortail> {
    const r = await axios.get(`${API_URL}/portail/paiements/${id}`, { headers: authHeader() });
    return r.data;
  }

  /** Télécharge le reçu PDF d'un paiement confirmé (renvoie un Blob à download) */
  async telechargerRecuPaiement(id: string): Promise<Blob> {
    const r = await axios.get(`${API_URL}/portail/paiements/${id}/recu`, {
      headers: authHeader(),
      responseType: 'blob',
    });
    return r.data;
  }

  /**
   * Télécharge le PDF d'une facture pour réédition.
   * Endpoint backend : GET /portail/factures/:id/pdf — auth abonné, renvoie
   * uniquement les factures appartenant à l'abonné connecté.
   */
  async telechargerFacturePdf(factureId: string): Promise<Blob> {
    const r = await axios.get(`${API_URL}/portail/factures/${factureId}/pdf`, {
      headers: authHeader(),
      responseType: 'blob',
    });
    return r.data;
  }

  /**
   * Simule la confirmation d'un paiement en mode développement.
   * Désactivé côté backend en production. À utiliser uniquement pour les tests.
   */
  async simulerConfirmationPaiementDev(id: string, resultat: 'SUCCESS' | 'FAILED' = 'SUCCESS'): Promise<any> {
    const r = await axios.post(
      `${API_URL}/portail/paiements/${id}/simuler-confirmation-dev`,
      { resultat },
      { headers: authHeader() },
    );
    return r.data;
  }

  async historique(statut?: StatutPaiement): Promise<PaiementPortail[]> {
    const r = await axios.get(`${API_URL}/portail/historique`, {
      headers: authHeader(),
      params: statut ? { statut } : {},
    });
    return r.data;
  }

  // ─── Profil ─────────────────────────────────────────────────────────
  async getProfil() {
    const r = await axios.get(`${API_URL}/portail/profil`, { headers: authHeader() });
    return r.data;
  }

  async modifierProfil(dto: { email?: string; telephone?: string; preferencesNotifications?: any }) {
    const r = await axios.put(`${API_URL}/portail/profil`, dto, { headers: authHeader() });
    return r.data;
  }

  // ─── Mai 2026 — Pont SIGP (cabinet juridique) ──────────────────────
  /**
   * Liste les prochaines audiences du justiciable connecté (max 5).
   * Best-effort : renvoie [] si SIGP indisponible.
   */
  async listerAudiences(): Promise<Array<{
    id: string;
    numeroAudience: string;
    typeAudience: string;
    dateAudience: string;
    heureDebut: string | null;
    salle: string | null;
    juridiction: string | null;
    numeroDossier: string | null;
  }>> {
    try {
      const r = await axios.get(`${API_URL}/portail/audiences-juridiques`, { headers: authHeader() });
      return Array.isArray(r.data) ? r.data : [];
    } catch {
      return [];
    }
  }

  // ─── MM-52 — Démarrage workflow depuis le portail abonné ─────────────
  /**
   * Récupère le formulaire de l'étape de saisie d'un type d'opération
   * exposé à l'abonné. Le backend résout le workflow par
   * (typeOperationCode + entité de l'abonné) et compose les champs hérités
   * + templates + champs propres.
   */
  async getEtapeSaisiePourType(typeOperationCode: string, slug?: string): Promise<{
    typeOperation: { code: string; label: string; description: string; categorie: string; domaine: string };
    workflow: { id: string; code: string; nom: string; nbEtapesTotal: number };
    etapeSaisie: { id: string; nom: string; ordre: number; delaiHeures: number };
    formConfig: { champs: any[]; modeHerite?: 'lecture' | 'modification' };
  }> {
    // MM-117d — Route 100% publique : slug en query si pas de token
    const headers = portailAuth.isAuthenticated() ? authHeader() : {};
    const params = slug ? { slug } : {};
    const r = await axios.get(
      `${API_URL}/portail-abonne/demandes-en-ligne/${encodeURIComponent(typeOperationCode)}/etape-saisie`,
      { headers, params },
    );
    return r.data;
  }

  /**
   * MM-53 — Liste les agences disponibles pour réception d'une demande
   * (sous-entités type='agence' dans la hiérarchie de l'entité de l'abonné).
   *
   * @deprecated MM-113 — Utiliser `listerDirectionsEtAgences()` pour bénéficier
   * de la cascade DR → Agence. Conservé pour rétro-compat des appels existants.
   */
  async listerAgencesPourDemande(): Promise<Array<{
    id: string;
    code: string;
    nom: string;
    ville?: string;
    type: string;
    parentId?: string;
  }>> {
    const r = await axios.get(
      `${API_URL}/portail-abonne/demandes-en-ligne/agences`,
      { headers: authHeader() },
    );
    return r.data?.agences || [];
  }

  /**
   * MM-113 + MM-117c — Retourne les DR + Agences. Fallback automatique vers
   * la route publique (par slug) si pas de token portail ou 401.
   *
   * @param slug Slug d'entité (ex: 'eec') — utilisé pour la route publique
   *             fallback si non authentifié.
   */
  async listerDirectionsEtAgences(slug?: string): Promise<{
    directions: Array<{ id: string; code: string; nom: string; ville?: string; type: string }>;
    agences: Array<{ id: string; code: string; nom: string; ville?: string; type: string; parentId?: string }>;
  }> {
    const tokenPresent = !!portailAuth.getToken();

    // Si pas de token et qu'on a un slug → route publique directement
    if (!tokenPresent && slug) {
      const r = await axios.get(
        `${API_URL}/portail-abonne/demandes-en-ligne/agences-publiques`,
        { params: { slug } },
      );
      return {
        directions: r.data?.directions || [],
        agences: r.data?.agences || [],
      };
    }

    try {
      const r = await axios.get(
        `${API_URL}/portail-abonne/demandes-en-ligne/agences`,
        { headers: authHeader() },
      );
      return {
        directions: r.data?.directions || [],
        agences: r.data?.agences || [],
      };
    } catch (e: any) {
      // MM-117c — Si 401 (token expiré) et qu'on a un slug, fallback public
      if (e?.response?.status === 401 && slug) {
        const r = await axios.get(
          `${API_URL}/portail-abonne/demandes-en-ligne/agences-publiques`,
          { params: { slug } },
        );
        return {
          directions: r.data?.directions || [],
          agences: r.data?.agences || [],
        };
      }
      throw e;
    }
  }

  /**
   * Crée une demande générique depuis le portail abonné OU le portail public.
   * Démarre automatiquement le workflow réel (pas un PortailDemande catch-all).
   * Retourne le numéro de demande généré pour confirmation à l'utilisateur.
   *
   * MM-53 — agenceId : obligatoire pour les demandes de travaux neufs (neuf),
   *   optionnel pour abonnement/branchement (backend résout l'agence depuis le contrat).
   * MM-117d — Le `slug` permet à un visiteur non authentifié de cibler l'entité.
   *           Si pas de token, on n'envoie pas d'header d'auth.
   * MM-NEW — abonnementId / branchementId : contrat sélectionné dans le portail ;
   *   le backend dérive automatiquement l'agenceId via ecom_branchements.agenceId.
   */
  async creerDemandeEnLigne(dto: {
    typeOperationCode: string;
    titre: string;
    donnees: Record<string, any>;
    /** Obligatoire pour travaux neufs, optionnel sinon (résolu depuis le contrat) */
    agenceId?: string;
    /** ID du contrat d'abonnement concerné (portail contexte=abonnement) */
    abonnementId?: string;
    /** MM-135 — Convergence sur contratAbonnementId (alias de abonnementId, rétro-compat) */
    contratAbonnementId?: string;
    /** ID du branchement concerné (portail contexte=branchement) */
    branchementId?: string;
    /** MM-117d — slug d'entité pour identifier l'opérateur côté portail public */
    slug?: string;
  }): Promise<{
    id: string;
    numeroDemande: string;
    numeroMetier: string | null;
    statut: string;
    etapeCouranteId: string | null;
    typeOperationCode: string;
    agence?: { id: string; nom: string };
  }> {
    const headers = portailAuth.isAuthenticated() ? authHeader() : {};
    const r = await axios.post(
      `${API_URL}/portail-abonne/demandes-en-ligne`,
      dto,
      { headers },
    );
    return r.data;
  }

  /**
   * Liste l'équipe d'un cabinet (endpoint PUBLIC, pas de token requis).
   * Utilisé par la vitrine cabinet pour la section "Notre équipe".
   */
  async listerEquipeCabinet(slug: string): Promise<Array<{
    id: string;
    nom: string;
    prenom: string | null;
    fonction: string;
    specialite: string | null;
    numeroOrdre: string | null;
    photoUrl: string | null;
    email: string | null;
  }>> {
    if (!slug) return [];
    try {
      const r = await axios.get(`${API_URL}/portail/equipe-cabinet/${encodeURIComponent(slug)}`);
      return Array.isArray(r.data) ? r.data : [];
    } catch {
      return [];
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // FONDS D'ÉPARGNE — volet II du CDCF
  // ══════════════════════════════════════════════════════════════════════
  //
  // Mêmes en-têtes, même jeton portail : ces routes vivent sous le guard
  // abonné du socle. Un jeton d'abonné eau ou électricité y est refusé côté
  // serveur — le type de compte est revérifié à chaque appel.

  async fondsMaSituation(): Promise<SituationFonds> {
    const r = await axios.get(`${API_URL}/public/portail/fonds-epargne/ma-situation`, {
      headers: authHeader(),
    });
    return r.data;
  }

  async fondsMesVersements(limite = 100): Promise<VersementFonds[]> {
    const r = await axios.get(`${API_URL}/public/portail/fonds-epargne/mes-versements`, {
      headers: authHeader(),
      params: { limite },
    });
    return Array.isArray(r.data) ? r.data : [];
  }

  async fondsMesDocuments(): Promise<DocumentFonds[]> {
    const r = await axios.get(`${API_URL}/public/portail/fonds-epargne/mes-documents`, {
      headers: authHeader(),
    });
    return Array.isArray(r.data) ? r.data : [];
  }

  /**
   * Ouvre un PDF du fonds dans un nouvel onglet.
   * Le jeton passe en query : un onglet ouvert par le navigateur ne porte
   * pas d'en-tête Authorization — le guard portail accepte les deux formes.
   */
  fondsUrlDocument(type: 'attestation' | 'releve' | 'historique', dateArrete?: string): string {
    const params = new URLSearchParams({ type, token: portailAuth.getToken() || '' });
    if (dateArrete) params.set('dateArrete', dateArrete);
    return `${API_URL}/public/portail/fonds-epargne/document.pdf?${params.toString()}`;
  }

  async fondsModifierProfil(dto: { telephone?: string; email?: string; adresse?: string }) {
    const r = await axios.patch(`${API_URL}/public/portail/fonds-epargne/mon-profil`, dto, {
      headers: authHeader(),
    });
    return r.data;
  }
}

// ─── Types Fonds d'Épargne ───────────────────────────────────────────────────

export interface SituationFonds {
  identite: {
    numeroSouscripteur: string | null;
    nom: string;
    prenoms: string | null;
    entiteOrigine: string | null;
    bureauRegional: string | null;
    dateAdhesion: string | null;
    statutKyc: string;
  };
  avoirs: {
    capitalVerse: number;
    interetsCumules: number;
    avoirTotal: number;
    tauxRendement: number;
  };
  activite: {
    nbVersements: number;
    dernierVersement: { date: string; montant: number } | null;
    nbPeriodesCapitalisees: number;
  };
}

export interface VersementFonds {
  id: string;
  dateOperation: string;
  dateValeur: string | null;
  montant: number;
  libelleBancaire: string | null;
  referenceBancaire: string | null;
}

export interface DocumentFonds {
  type: 'attestation' | 'releve' | 'historique';
  libelle: string;
  date: string | null;
  parametres?: Record<string, string>;
}

export const espaceClientService = new EspaceClientService();
